import moment from "moment-timezone";
import { Col } from "reactstrap";
import dragIIcon from "../../../../assets/images/drag-icon.svg";
import cursorPointer from "../../../../assets/images/cursor.svg";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import styles from "./index.module.scss";
import { SessionsService } from "../../../services/sessions.service";

interface Session {
    _id: string;
    startDate: string;
    startTime: string;
    endTime: string;
    sessionType?: string;
    sessionStatus?: string;
    sessionTitle?: string;
    sessionPrivacy?: string;
    sessionDescription?: string;
    related?: any[];
    order?: number;
}

interface GroupedSessions {
    [key: string]: Session[];
}

interface SessionListProps {
    seminarCards: Session[];
    openCurrentSession: (item: Session) => void;
    onOrderUpdate: (updatedSessions: Session[]) => void;
}

const SessionList = ({ seminarCards, openCurrentSession, onOrderUpdate }: SessionListProps) => {
    const groupedSessions: GroupedSessions = seminarCards.reduce((groups, session) => {
        if (!session.startDate) {
            return groups;
        }

        // Handle both ISO and MM/DD/YYYY formats
        let dateKey;
        if (session.startDate.includes('/')) {
            const [month, day, year] = session.startDate.split('/');
            dateKey = moment(`${year}-${month}-${day}`).format("D MMM, YYYY");
        } else {
            dateKey = moment(session.startDate).format("D MMM, YYYY");
        }

        if (!groups[dateKey]) {
            groups[dateKey] = [];
        }
        groups[dateKey].push(session);
        return groups;
    }, {} as GroupedSessions);

    Object.keys(groupedSessions).forEach((dateKey) => {
        const sessions = groupedSessions[dateKey];

        sessions.sort((a, b) => {
            const aTime = moment(a.startTime, "hh:mm A");
            const bTime = moment(b.startTime, "hh:mm A");
            const aEndTime = moment(a.endTime, "hh:mm A");
            const bEndTime = moment(b.endTime, "hh:mm A");

            if (aTime.isSame(bTime) && aEndTime.isSame(bEndTime)) {
                return (a.order ?? 0) - (b.order ?? 0);
            }

            if (aTime.isSame(bTime)) {
                return aEndTime.diff(bEndTime);
            }
            return aTime.diff(bTime);
        });

        sessions.forEach((session, index) => {
            if (session.order === undefined) {
                session.order = index;
                SessionsService().updateSessionOrder(session._id, index);
            }
        });
    });

    const sortedDates = Object.keys(groupedSessions).sort((a, b) => {
        const aMoment = moment(a, "D MMM, YYYY");
        const bMoment = moment(b, "D MMM, YYYY");
        return aMoment.diff(bMoment);
    });


    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const sourceDate = source.droppableId;
        const destDate = destination.droppableId;

        if (sourceDate !== destDate) return;

        const sessions = groupedSessions[sourceDate];
        const sourceTime = moment(sessions[source.index].startTime, "hh:mm A");
        const destTime = moment(sessions[destination.index].startTime, "hh:mm A");
        const sourceEndTime = moment(sessions[source.index].endTime, "hh:mm A");
        const destEndTime = moment(sessions[destination.index].endTime, "hh:mm A");

        if (!sourceTime.isSame(destTime) || !sourceEndTime.isSame(destEndTime)) return;

        const items = Array.from(sessions);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);

        items.forEach(async (item, index) => {
            item.order = index;
            await SessionsService().updateSessionOrder(item._id, index);
        });

        groupedSessions[sourceDate] = items;
        onOrderUpdate(items);
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className={styles.sessionListTitle}>
                <img src={dragIIcon} alt="Drag Icon" />
                <img className={styles.cursorPointer} src={cursorPointer} alt="Cursor" />
                <span>If there are sessions scheduled at the same time, you can drag them to rearrange the schedule.</span>
            </div>

            <div className={styles.sessionListContainer}>
                {sortedDates.map((dateKey) => (
                    <div key={dateKey} className={styles.sessionList}>
                        <div className={styles.sessionListDate}>
                            {dateKey}
                        </div>
                        <Droppable droppableId={dateKey}>
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {groupedSessions[dateKey].map((session: Session, index: number) => {
                                        const isDraggable = groupedSessions[dateKey].some((other, otherIndex) =>
                                            index !== otherIndex &&
                                            moment(session.startTime, "hh:mm A").isSame(moment(other.startTime, "hh:mm A")) &&
                                            moment(session.endTime, "hh:mm A").isSame(moment(other.endTime, "hh:mm A"))
                                        );
                                        return (
                                            <Draggable
                                                key={`${dateKey}-${session._id}-${index}`}
                                                draggableId={`${dateKey}-${session._id}-${index}`}
                                                index={index}
                                                isDragDisabled={!isDraggable}
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <Col md="10" className={styles.sessionItemContainer}>
                                                            {isDraggable && (<div
                                                                className={styles.dragHandle}
                                                                {...provided.dragHandleProps}
                                                            >
                                                                <img src={dragIIcon} alt="Drag Handle" />
                                                            </div>)}
                                                            <div
                                                                className={`${styles.sessionItem} ${isDraggable ? styles.draggable : ""}`}
                                                                onClick={() => openCurrentSession(session)}>
                                                                <div className={styles.sessionInfo}>
                                                                    <span className={styles.sessionTime}>
                                                                        {moment(session.startTime, "hh:mm A").isValid() && moment(session.endTime, "hh:mm A").isValid()
                                                                            ? `${moment(session.startTime, "hh:mm A").format("HH:mm")} - ${moment(session.endTime, "hh:mm A").format("HH:mm")}`
                                                                            : "Invalid time"}
                                                                    </span>
                                                                    {session.sessionType && <span className={styles.sessionType}>{session.sessionType}</span>}
                                                                    <span className={`${styles.sessionStatus} ${session.sessionStatus === "Hidden" ? styles.hidden : styles.active}`}>
                                                                        {session.sessionStatus ? session.sessionStatus : "Active"}
                                                                    </span>
                                                                </div>
                                                                <div className={styles.sessionInfo}>
                                                                    <span className={styles.sessionTitle}>{session.sessionTitle || "no title"}</span>
                                                                    <span className={`${styles.sessionPrivacy} ${styles[session.sessionPrivacy?.replace(" ", "") || "Open"]}`}>
                                                                        {session.sessionPrivacy || "No privacy"}
                                                                    </span>
                                                                </div>
                                                                <div className={`${styles.sessionInfo} ${styles.sessionItems}`}>
                                                                    <span className={styles.sessionItemsCount}>{session.related?.length || "0"} Items</span>
                                                                    {session.sessionDescription && <span>{session.sessionDescription}</span>}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                ))}
            </div>
        </DragDropContext>
    );
};

export default SessionList;